/**
 * QUIL — session sync log (feature B).
 *
 * A read-only, in-memory ring buffer of this session's sync outcomes so that when
 * the top-bar pill says "Sync failed", Kamrin can open Settings, read the actual
 * error, and paste it back. Deliberately NOT persisted — logging must never be
 * able to fail a save, and a fresh session starting clean is fine.
 *
 * It registers itself as the sink for `syncMonitor.logSync`, so every completed
 * load/pull/write the engine reports lands here automatically. Exposed to React
 * via a `useSyncExternalStore` adapter.
 */

import { useSyncExternalStore } from 'react'
import { registerLogSink, type SyncEventKind } from './syncMonitor'

export interface SyncLogEntry {
  seq: number            // stable, monotonic — a good React key
  at: number             // epoch ms
  kind: SyncEventKind    // load | pull | write
  ok: boolean
  note?: string          // optional error text on failure
}

export interface SyncLogSnapshot {
  entries: SyncLogEntry[]   // newest first
  lastLoadOk?: number       // last successful load/pull (a read from OneDrive)
  lastSaveOk?: number       // last successful write (a save to OneDrive)
}

const CAP = 50

let entries: SyncLogEntry[] = []
let lastLoadOk: number | undefined
let lastSaveOk: number | undefined
let seq = 0
let snapshot: SyncLogSnapshot = { entries, lastLoadOk, lastSaveOk }

const listeners = new Set<() => void>()

function commit(): void {
  snapshot = { entries, lastLoadOk, lastSaveOk }
  for (const l of listeners) l()
}

function record(kind: SyncEventKind, ok: boolean, note: string | undefined, at: number): void {
  entries = [{ seq: seq++, at, kind, ok, note }, ...entries].slice(0, CAP)
  if (ok && (kind === 'load' || kind === 'pull')) lastLoadOk = at
  if (ok && kind === 'write') lastSaveOk = at
  commit()
}

// Self-register on import so the whole session is captured, not just from when
// Settings first mounts. Imported for its side-effect in App.
registerLogSink(record, () => Date.now())

function subscribeSyncLog(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

/** @internal Exported for its unit tests; production reaches it only from inside this module. */
export function getSyncLogSnapshot(): SyncLogSnapshot {
  return snapshot
}

export function useSyncLog(): SyncLogSnapshot {
  return useSyncExternalStore(subscribeSyncLog, getSyncLogSnapshot, getSyncLogSnapshot)
}

/** Test-only: clear the buffer between cases.
 * @internal Test seam: resets module state between tests. Never imported by the app.
 */
export function resetSyncLog(): void {
  entries = []
  lastLoadOk = undefined
  lastSaveOk = undefined
  seq = 0
  commit()
}
